import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { getStoryblokApi } from '@storyblok/react'
import type { ISbStoryData } from '@storyblok/js'
import type { Page } from '@percent/cause-dashboard/storyblok/types/storyblok-components'
import { env } from '@percent/cause-dashboard/env'
import { isStoryblokPreviewSearch } from '@percent/cause-dashboard/storyblok/helpers/isStoryblokPreviewSearch'
import '../storyblokInit'

export type HomePageStoryState = {
  story: ISbStoryData<Page> | null
  loading: boolean
  error: Error | null
  isPreview: boolean
  refetch: () => void
}

const FULL_SLUG = 'causes-portal-pages/homepage'

export const STORYBLOK_RESOLVE_RELATIONS = ['spotlight_banner.offer']

export function useHomePageStory(enabled: boolean): HomePageStoryState {
  const [searchParams] = useSearchParams()
  const isPreview = isStoryblokPreviewSearch(searchParams)

  const [story, setStory] = useState<ISbStoryData<Page> | null>(null)
  const [loading, setLoading] = useState(enabled)
  const [error, setError] = useState<Error | null>(null)
  const [fetchId, setFetchId] = useState(0)

  const refetch = useCallback(() => {
    setFetchId(n => n + 1)
  }, [])

  useEffect(() => {
    if (!enabled) {
      setStory(null)
      setError(null)
      setLoading(false)
      return
    }

    const publishedToken = env.NX_PUBLIC_STORYBLOK_PUBLIC_ACCESS_TOKEN
    const previewToken = env.NX_PUBLIC_STORYBLOK_PREVIEW_ACCESS_TOKEN

    if (isPreview && !previewToken) {
      setStory(null)
      setError(new Error('Storyblok env missing for preview: NX_PUBLIC_STORYBLOK_PREVIEW_ACCESS_TOKEN'))
      setLoading(false)
      return
    }

    if (!isPreview && !publishedToken) {
      setStory(null)
      setError(new Error('Storyblok env missing: NX_PUBLIC_STORYBLOK_PUBLIC_ACCESS_TOKEN'))
      setLoading(false)
      return
    }

    const token = isPreview ? previewToken : publishedToken
    const version = isPreview ? 'draft' : 'published'

    let cancelled = false
    setLoading(true)
    setError(null)

    const api = getStoryblokApi()
    api
      .get(`cdn/stories/${FULL_SLUG}`, {
        version,
        resolve_relations: STORYBLOK_RESOLVE_RELATIONS,
        token
      })
      .then(res => {
        if (cancelled) return
        const s = res.data?.story as ISbStoryData<Page> | undefined
        if (!s?.content || s.content.component !== 'page') {
          setError(new Error('Storyblok: invalid page story response'))
          setStory(null)
          return
        }
        setStory(s)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setError(err instanceof Error ? err : new Error(String(err)))
        setStory(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [enabled, fetchId, isPreview])

  return { story, loading, error, isPreview, refetch }
}
